import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Clock, TrendingUp, Info } from "lucide-react";

export const FundingRates = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-3xl font-bold mb-2">Funding Rates</h1>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Understand funding rates, how they work, and when they're paid</p>
        </div>

        {/* What Are Funding Rates */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">What Are Funding Rates?</h2>
          <p className="text-muted-foreground mb-6">
            Funding rates are periodic payments exchanged between traders holding long and short positions. 
            They're the mechanism that keeps perpetual contract prices aligned with the underlying spot prices.
          </p>
          
          <p className="text-muted-foreground mb-6">
            Since perpetual contracts have no expiration date, funding rates create an incentive for 
            traders to take positions that help balance the market and keep the perpetual price close 
            to the spot price.
          </p>

          <div className="p-6 border border-border rounded-lg bg-card">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold text-lg">On Nordex</h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-2 pl-4 list-disc">
              <li>Funding occurs every <strong className="text-foreground">8 hours</strong></li>
              <li>Maximum funding rate: <strong className="text-foreground">±0.75%</strong> per interval</li>
              <li>Rates are based on the difference between perpetual and spot prices</li>
            </ul>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How Funding Rates Work</h2>
          
          <div className="space-y-4 mb-6">
            <div className="p-6 border border-emerald-500/20 rounded-lg bg-emerald-500/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-semibold text-lg">Positive Funding Rate</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                When the perpetual price is <strong className="text-foreground">higher</strong> than the spot price:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                <li><strong className="text-red-500">Long positions PAY</strong> funding to shorts</li>
                <li><strong className="text-emerald-500">Short positions RECEIVE</strong> funding from longs</li>
                <li>This incentivizes shorts and discourages longs, pushing the price down</li>
              </ul>
            </div>

            <div className="p-6 border border-red-500/20 rounded-lg bg-red-500/5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-red-500/10">
                  <TrendingUp className="h-6 w-6 text-red-500 rotate-180" />
                </div>
                <h3 className="font-semibold text-lg">Negative Funding Rate</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                When the perpetual price is <strong className="text-foreground">lower</strong> than the spot price:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4 list-disc">
                <li><strong className="text-red-500">Short positions PAY</strong> funding to longs</li>
                <li><strong className="text-emerald-500">Long positions RECEIVE</strong> funding from shorts</li>
                <li>This incentivizes longs and discourages shorts, pushing the price up</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Funding Calculation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Calculating Funding Payments</h2>
          
          <p className="text-muted-foreground mb-4">
            The funding payment you pay or receive is calculated based on your position size and 
            the current funding rate:
          </p>

          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold text-lg mb-3">Formula</h3>
            <div className="p-4 bg-muted/30 rounded-lg font-mono text-sm">
              Funding Payment = Position Size × Funding Rate
            </div>
          </div>

          <div className="p-6 border border-border rounded-lg bg-card">
            <h3 className="font-semibold mb-4">Example:</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Position Size:</span>
                <span className="font-semibold">$10,000 (Long BTC)</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Current Funding Rate:</span>
                <span className="font-semibold">+0.01%</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <span className="font-semibold">You Pay:</span>
                <span className="font-bold text-red-500">$1</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Calculation: $10,000 × 0.01% = $1 paid to short positions every 8 hours
            </p>
          </div>
        </section>

        {/* Funding Intervals */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Funding Payment Schedule</h2>
          
          <p className="text-muted-foreground mb-6">
            On Nordex, funding payments occur every 8 hours at the following times (UTC):
          </p>

          <div className="grid gap-3 md:grid-cols-3">
            <div className="p-4 border border-border rounded-lg bg-card text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-primary" />
              <div className="font-semibold">00:00 UTC</div>
              <div className="text-xs text-muted-foreground mt-1">Midnight</div>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-primary" />
              <div className="font-semibold">08:00 UTC</div>
              <div className="text-xs text-muted-foreground mt-1">Morning</div>
            </div>
            <div className="p-4 border border-border rounded-lg bg-card text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-primary" />
              <div className="font-semibold">16:00 UTC</div>
              <div className="text-xs text-muted-foreground mt-1">Afternoon</div>
            </div>
          </div>

          <div className="p-4 bg-muted/30 rounded-lg mt-6">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                You only pay or receive funding if you hold a position at the time of the funding 
                interval. If you close your position before the funding time, you won't be affected.
              </p>
            </div>
          </div>
        </section>

        {/* Viewing Funding Rates */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Where to View Funding Rates</h2>
          
          <p className="text-muted-foreground mb-4">
            You can monitor funding rates in several places on Nordex:
          </p>

          <div className="space-y-3">
            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Funding Rate Panel</h3>
              <p className="text-sm text-muted-foreground">
                Add the Funding Rate panel to your layout (click the "+" button) to see the current 
                funding rate, time until next payment, and a brief explanation.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Market Selector</h3>
              <p className="text-sm text-muted-foreground">
                The market selector dropdown shows the current funding rate for each market, 
                allowing you to compare rates across different assets.
              </p>
            </div>

            <div className="p-4 border border-border rounded-lg">
              <h3 className="font-semibold mb-2">Order Form</h3>
              <p className="text-sm text-muted-foreground">
                When placing an order, you'll see the current funding rate displayed, so you know 
                what you might pay or receive before opening a position.
              </p>
            </div>
          </div>
        </section>

        {/* Funding Strategies */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Funding Rate Strategies</h2>
          
          <div className="space-y-4">
            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">1. Avoiding High Funding Costs</h3>
              <p className="text-sm text-muted-foreground">
                If you're holding a position long-term and funding rates are consistently against you, 
                consider closing before funding times and reopening after, or switching to the opposite 
                side if your thesis allows.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">2. Funding Rate Arbitrage</h3>
              <p className="text-sm text-muted-foreground">
                Advanced traders can earn funding payments by taking positions that collect funding. 
                For example, if funding is high and positive, shorting the perpetual while longing 
                spot can earn the funding payment with minimal directional risk.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">3. Timing Entries</h3>
              <p className="text-sm text-muted-foreground">
                If funding rates are extreme, it might indicate an overcrowded trade. Very high positive 
                funding suggests too many longs, which could precede a correction. Very negative funding 
                suggests too many shorts.
              </p>
            </div>

            <div className="p-6 border border-border rounded-lg bg-card">
              <h3 className="font-semibold text-lg mb-3">4. Short-Term Trading</h3>
              <p className="text-sm text-muted-foreground">
                If you're scalping or day trading, funding rates are less of a concern since you're 
                typically closing positions within hours. Focus more on price action and execution.
              </p>
            </div>
          </div>
        </section>

        {/* Important Considerations */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Important Points About Funding</h2>
          
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Funding is between traders</strong> -- payments are exchanged between 
              traders, not paid to or collected by the platform. It's a zero-sum transfer.
            </li>
            <li>
              <strong className="text-foreground">Rates can change</strong> -- funding rates are recalculated periodically based 
              on market conditions. A rate that's positive now could turn negative (or vice versa) 
              in the next interval.
            </li>
            <li>
              <strong className="text-foreground">Accumulates over time</strong> -- while individual funding payments might seem 
              small, they accumulate. A 0.01% rate every 8 hours equals about 1.1% monthly.
            </li>
            <li>
              <strong className="text-foreground">Close before funding to avoid</strong> -- if you want to avoid a funding payment, 
              close your position before the funding time and reopen after if desired.
            </li>
          </ul>
        </section>

        <DocsNavigation 
          previous={{ title: "Margin & Liquidation", href: "/margin-liquidation" }}
          next={{ title: "Order Types", href: "/order-types" }}
        />
      </div>
    </DocsLayout>
  );
};

